import { useContext } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useTranslate } from '@/hooks/useTranslate';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/utils/cn';
import {
  formatTaxPrice,
  formatTaxPercent,
  formatDiscountPrice,
  formatPriceWithCurrency,
  formatSubtotalPrice,
} from '@/utils/pricing';
import { Divider } from '@/components/Common/Divider';
import { BillingFrequency } from '@/types/subscription';

// "per {billingFrequency}" needs the singular noun ("per month"), not the
// adverb form billingFrequency carries on the wire ("monthly" -> "per monthly"
// reads wrong). The "Billed {billingFrequency}" line below is unaffected —
// "Billed monthly" is already correct English with the raw adverb.
const BILLING_FREQUENCY_TO_PERIOD_KEY: Record<BillingFrequency, string> = {
  [BillingFrequency.DAILY]: 'periodDay',
  [BillingFrequency.WEEKLY]: 'periodWeek',
  [BillingFrequency.MONTHLY]: 'periodMonth',
  [BillingFrequency.YEARLY]: 'periodYear',
};

/**
 * Subscriptions-only order summary (ported from independent-checkout-v2's
 * `SubscriptionsDescription`): plan name/price header, a bold "{plan}
 * Subscription" line item, the billing frequency, then the same
 * subtotal/discount/tax breakdown as one-time checkout but ending in
 * "Total due today" instead of "Total". Renders in place of
 * `PlayerDetails` + the generic `PriceDetails`/`TotalPrice` stack, which have
 * no subscription line item and no `totalDueToday` copy.
 */
export const SubscriptionDetails = () => {
  const { subscription, pricing, template } = useContext(CheckoutContext);
  const { t } = useTranslate();
  const isMobile = useIsMobile();

  const plan = subscription?.plan;
  const planDisplayName = plan?.displayName ?? '';
  const billingFrequency = plan?.billingFrequency ?? '';
  const priceBreakdown = pricing.priceBreakdown;
  const showDiscount = Boolean(priceBreakdown.discount);
  const isTaxValid = priceBreakdown.isTaxValid;
  const totalPrice = formatPriceWithCurrency(priceBreakdown.priceTotal, pricing);
  const periodKey = plan?.billingFrequency ? BILLING_FREQUENCY_TO_PERIOD_KEY[plan.billingFrequency] : undefined;
  // Mirrors Details.tsx's `wrapperClassName`: on mobile the container's own
  // padding provides the gutter (this stays full-width); on desktop the
  // container drops its padding and centers its children instead, so the
  // wrapper itself must carry the width constraint (`md:w-5/6`) or the
  // breakdown sits flush against the hero edge.
  const wrapperClassName = isMobile ? template.styles.detailsWrapper.mobile : template.styles.detailsWrapper.desktop;

  return (
    <div data-testid="subscription-details-section" className={cn('flex flex-col', wrapperClassName)}>
      <div
        data-testid="subscription-plan-title"
        className="font-semibold text-base truncate"
        title={`${t('subscribeTo')} ${planDisplayName}`}
      >
        {t('subscribeTo')} {planDisplayName}
      </div>
      <div className="flex flex-row items-center gap-1.5 mt-1">
        <span className="text-2xl font-semibold" data-testid="total-price-header">
          {totalPrice}
        </span>
        <span className="text-sm" data-testid="subscription-billing-period">
          {t('per')} {periodKey ? t(periodKey) : billingFrequency}
        </span>
      </div>

      <div className="flex justify-between mt-4 font-semibold text-sm" data-testid="subscription-line-item">
        <span>
          {planDisplayName} {t('subscription')}
        </span>
        <span>{totalPrice}</span>
      </div>
      <div className="text-sm mt-1" data-testid="subscription-billed-frequency">
        {t('billed')} {billingFrequency}
      </div>

      <div className="mt-4">
        <Divider />
      </div>

      <div className="flex flex-col gap-2 text-sm mt-4">
        <div className="flex justify-between">
          <div>{t('subtotal')}</div>
          <div data-testid="subtotal-price">{formatSubtotalPrice(priceBreakdown, pricing)}</div>
        </div>
        {showDiscount && (
          <div className="flex justify-between">
            <div>{t('firstPaymentDiscount')}</div>
            <div data-testid="discount">-{formatDiscountPrice(priceBreakdown, pricing)}</div>
          </div>
        )}
        <div className="flex justify-between">
          <div data-testid="tax-percent">
            {t('tax')} {isTaxValid ? `(${formatTaxPercent(priceBreakdown)})` : ''}
          </div>
          <div data-testid="tax-price">{formatTaxPrice(priceBreakdown, pricing)}</div>
        </div>
      </div>

      <div className="mt-4">
        <Divider />
      </div>

      <div className="flex justify-between mt-4 font-bold text-md">
        <div>{t('totalDueToday')}</div>
        <div data-testid="total-price">{totalPrice}</div>
      </div>
    </div>
  );
};
